import { useState, useEffect } from 'react'
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom'
import AnimatedRoutes from '../components/AnimatedRoutes'
import DemoModal from '../components/DemoModal'
import SignupModal from '../components/SignupModal'
import LandscapeLock from '../components/LandscapeLock'
import Navigation from '../landing/components/Navigation'
import { trackPageView, trackCtaClick } from '../lib/analytics'

function RouteAnalytics() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location.pathname, location.search])
  return null
}

function InternalLinkInterceptor() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.defaultPrevented) return
      if (e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const link = (e.target as HTMLElement).closest('a')
      if (!link) return
      if (link.target && link.target !== '_self') return
      if (link.hasAttribute('download')) return

      const href = link.getAttribute('href')
      if (!href) return
      if (href.startsWith('#demo')) return
      if (href.startsWith('#start')) return
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return

      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin) return

      const samePath = url.pathname === location.pathname
      if (samePath && url.hash) {
        // Let the browser handle in-page anchor jumps.
        return
      }

      e.preventDefault()
      navigate(url.pathname + url.search + url.hash)
    }

    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [navigate, location.pathname])

  return null
}

export default function App() {
  const [demoOpen, setDemoOpen] = useState(false)
  const [signupOpen, setSignupOpen] = useState(false)

  useEffect(() => {
    // Stop the browser from auto-restoring scroll on back/forward nav;
    // we manage scroll ourselves via AnimatedRoutes + LandingPage mount.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const demoLink = target.closest('a[href="#demo"]')
      if (demoLink) {
        e.preventDefault()
        trackCtaClick('request_a_demo', window.location.pathname)
        setDemoOpen(true)
        return
      }
      const signupLink = target.closest('a[href="#start"]')
      if (signupLink) {
        e.preventDefault()
        trackCtaClick('start_for_free', window.location.pathname)
        setSignupOpen(true)
      }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  return (
    <BrowserRouter>
      <RouteAnalytics />
      <InternalLinkInterceptor />
      <Navigation />
      <AnimatedRoutes />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
      <LandscapeLock />
    </BrowserRouter>
  )
}
